/** 多边形基础运算：面积、包围盒、简化、点内判定、自交检测、基本形状构造。
 * 所有函数使用毫米坐标，不依赖渲染环境，可独立单测。
 */
import type { Polygon, Vec } from '../types';

export const v = (x: number, y: number): Vec => ({ x, y });

export const add = (a: Vec, b: Vec): Vec => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
export const scale = (a: Vec, k: number): Vec => ({ x: a.x * k, y: a.y * k });
export const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
export const cross = (a: Vec, b: Vec): number => a.x * b.y - a.y * b.x;
export const lengthOf = (a: Vec): number => Math.hypot(a.x, a.y);
export const distance = (a: Vec, b: Vec): number => Math.hypot(a.x - b.x, a.y - b.y);
export const lerp = (a: Vec, b: Vec, t: number): Vec => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

/** 有符号面积；逆时针为正。 */
export function signedArea(poly: Polygon): number {
  let s = 0;
  for (let i = 0, n = poly.length; i < n; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % n];
    s += p.x * q.y - q.x * p.y;
  }
  return s / 2;
}

export const area = (poly: Polygon): number => Math.abs(signedArea(poly));

export function perimeter(poly: Polygon): number {
  let s = 0;
  for (let i = 0, n = poly.length; i < n; i++) s += distance(poly[i], poly[(i + 1) % n]);
  return s;
}

export function centroid(poly: Polygon): Vec {
  const a = signedArea(poly);
  if (Math.abs(a) < 1e-12) {
    const s = poly.reduce((acc, p) => add(acc, p), v(0, 0));
    return scale(s, 1 / Math.max(poly.length, 1));
  }
  let cx = 0;
  let cy = 0;
  for (let i = 0, n = poly.length; i < n; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % n];
    const f = p.x * q.y - q.x * p.y;
    cx += (p.x + q.x) * f;
    cy += (p.y + q.y) * f;
  }
  return { x: cx / (6 * a), y: cy / (6 * a) };
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export const EMPTY_BOUNDS: Bounds = {
  minX: Infinity,
  minY: Infinity,
  maxX: -Infinity,
  maxY: -Infinity,
};

export function boundsOf(points: Vec[]): Bounds {
  const b = { ...EMPTY_BOUNDS };
  for (const p of points) {
    if (p.x < b.minX) b.minX = p.x;
    if (p.y < b.minY) b.minY = p.y;
    if (p.x > b.maxX) b.maxX = p.x;
    if (p.y > b.maxY) b.maxY = p.y;
  }
  return b;
}

export function unionBounds(a: Bounds, b: Bounds): Bounds {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

export function boundsCenter(b: Bounds): Vec {
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
}

/** 射线法点内判定，边界上的点行为不作保证。 */
export function pointInPolygon(p: Vec, poly: Polygon): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    const intersects = a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** 点到多边形边界的最短距离（正数，不含内外符号）。 */
export function distanceToPolygonEdge(p: Vec, poly: Polygon): number {
  let best = Infinity;
  for (let i = 0, n = poly.length; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    const ab = sub(b, a);
    const len2 = dot(ab, ab);
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, dot(sub(p, a), ab) / len2));
    best = Math.min(best, distance(p, add(a, scale(ab, t))));
  }
  return best;
}

/** 去重相邻点（含首尾闭合处），容差单位毫米。 */
export function dedupe(points: Vec[], eps = 0.05): Polygon {
  const out: Vec[] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (!last || distance(last, p) > eps) out.push(p);
  }
  while (out.length > 1 && distance(out[0], out[out.length - 1]) <= eps) out.pop();
  return out;
}

/** Ramer–Douglas–Peucker 路径简化，避免自由绘制产生海量顶点。 */
export function simplify(points: Vec[], toleranceMm: number): Polygon {
  if (points.length <= 3) return [...points];
  const tol2 = toleranceMm * toleranceMm;

  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;

  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop()!;
    let maxDist = 0;
    let index = -1;
    const a = points[first];
    const b = points[last];
    const ab = sub(b, a);
    const len2 = dot(ab, ab);
    for (let i = first + 1; i < last; i++) {
      const p = points[i];
      let d: number;
      if (len2 === 0) {
        d = distance(p, a);
      } else {
        const t = Math.max(0, Math.min(1, dot(sub(p, a), ab) / len2));
        d = distance(p, add(a, scale(ab, t)));
      }
      const d2 = d * d;
      if (d2 > maxDist) {
        maxDist = d2;
        index = i;
      }
    }
    if (index !== -1 && maxDist > tol2) {
      keep[index] = 1;
      stack.push([first, index], [index, last]);
    }
  }

  const out: Vec[] = [];
  for (let i = 0; i < points.length; i++) if (keep[i]) out.push(points[i]);
  return out;
}

function segmentIntersect(a: Vec, b: Vec, c: Vec, d: Vec): boolean {
  const d1 = cross(sub(b, a), sub(c, a));
  const d2 = cross(sub(b, a), sub(d, a));
  const d3 = cross(sub(d, c), sub(a, c));
  const d4 = cross(sub(d, c), sub(b, c));
  return d1 * d2 < 0 && d3 * d4 < 0;
}

/** 检测自交（相邻边共享端点不算）。O(n²)，仅用于输入校验。 */
export function isSelfIntersecting(poly: Polygon): boolean {
  const n = poly.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a1 = poly[i];
    const a2 = poly[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      if (j === i || (j + 1) % n === i || j === (i + 1) % n) continue;
      const b1 = poly[j];
      const b2 = poly[(j + 1) % n];
      if (segmentIntersect(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}

export function circlePolygon(center: Vec, radius: number, segments = 64): Polygon {
  const out: Vec[] = [];
  const n = Math.max(8, Math.round(segments));
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    out.push({ x: center.x + Math.cos(t) * radius, y: center.y + Math.sin(t) * radius });
  }
  return out;
}

export function rectPolygon(center: Vec, width: number, height: number, rotationRad = 0): Polygon {
  const hw = width / 2;
  const hh = height / 2;
  const local = [v(-hw, -hh), v(hw, -hh), v(hw, hh), v(-hw, hh)];
  const c = Math.cos(rotationRad);
  const s = Math.sin(rotationRad);
  return local.map((p) => ({
    x: center.x + p.x * c - p.y * s,
    y: center.y + p.x * s + p.y * c,
  }));
}

export function trianglePolygon(center: Vec, radius: number, rotationRad = 0): Polygon {
  const out: Vec[] = [];
  for (let i = 0; i < 3; i++) {
    const t = rotationRad + (i / 3) * Math.PI * 2;
    out.push({ x: center.x + Math.cos(t) * radius, y: center.y + Math.sin(t) * radius });
  }
  return out;
}

export function regularPolygon(center: Vec, radius: number, sides: number, rotationRad = 0): Polygon {
  const out: Vec[] = [];
  for (let i = 0; i < sides; i++) {
    const t = rotationRad + (i / sides) * Math.PI * 2;
    out.push({ x: center.x + Math.cos(t) * radius, y: center.y + Math.sin(t) * radius });
  }
  return out;
}

/** 扇形楔（从 baseAngle 起、张角 sweep），半径足够大以覆盖纸张。 */
export function wedgePolygon(baseAngleRad: number, sweepRad: number, radius: number): Polygon {
  const steps = Math.max(2, Math.ceil(sweepRad / (Math.PI / 64)));
  const out: Vec[] = [{ x: 0, y: 0 }];
  for (let i = 0; i <= steps; i++) {
    const t = baseAngleRad + (sweepRad * i) / steps;
    out.push({ x: Math.cos(t) * radius, y: Math.sin(t) * radius });
  }
  return out;
}

export function translatePolygon(poly: Polygon, dx: number, dy: number): Polygon {
  return poly.map((p) => ({ x: p.x + dx, y: p.y + dy }));
}

export function transformPolygon(poly: Polygon, fn: (p: Vec) => Vec): Polygon {
  return poly.map(fn);
}

/** SVG path d 属性，闭合。 */
export function polygonToPath(poly: Polygon): string {
  if (!poly.length) return '';
  const [first, ...rest] = poly;
  let d = `M${first.x} ${first.y}`;
  for (const p of rest) d += `L${p.x} ${p.y}`;
  return `${d}Z`;
}

/** 平均边长，用于判断"过小剪口"。 */
export function meanEdgeLength(poly: Polygon): number {
  if (poly.length < 2) return 0;
  return perimeter(poly) / poly.length;
}